import React, { useContext, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { FormDirty } from "go-form/components/FormDirty";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	StocktakingDraftApi,
	StocktakingDraftType,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../services/Api/api";
import { StocktakingContext } from "../../../../Preview";

interface Props {
	isShown: boolean;
	onHide: () => void;
	stocktakingId: number;
	handleSuccessAction: () => void;
}

const CreateStocktakingDraftModalForm = ({ isShown, onHide, stocktakingId, handleSuccessAction }: Props) => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<StocktakingDraftApi>({
		criteriaMode: "all",
		defaultValues: {
			type: StocktakingDraftType.DEFAULT,
		},
	});
	const {
		formState,
		formState: { errors },
		register,
		handleSubmit,
		setError,
	} = form;
	const [selectedType, setSelectedType] = useState<StocktakingDraftType>(StocktakingDraftType.DEFAULT);
	const [loading, setLoading] = useState<boolean>(false);
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const stocktakingContext = useContext(StocktakingContext);

	const onSubmit = handleSubmit(async (data: StocktakingDraftApi) => {
		setLoading(true);
		try {
			const newData = {
				...data,
				type: selectedType,
			};
			const res = await api.organization().createStocktakingDraft(stocktakingId, newData);
			addSuccessFlash(t("lib:common.flash.saved"));
			handleSuccessAction();
			onHide();
			handleChangeTabTitle(
				`${stocktakingContext.stocktaking.document_number} | ${t("modules.stocktaking.header.title")} | ${
					res.name
				}`
			);
			history.push(`/${organization.id}/stocktakings/${stocktakingId}/drafts/${res.id}`);
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<Modal onHide={onHide} show={isShown}>
			<FormDirty formState={formState} key="stocktaking-draft-form">
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.stocktaking_draft.field.new_draft.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormInput name="name" errors={errors} register={register} label={t("lib:common.word.name")} />
					<h5 className="mt-3">{t("modules.stocktaking_draft.field.draft_type.title")}</h5>
					<div className="d-flex flex-column mb-3">
						<Form.Check
							label={t("enums.stocktaking.drafts.type.DEFAULT")}
							type="radio"
							id="DEFAULT"
							className="form-check"
							checked={selectedType === "DEFAULT"}
							onChange={() => setSelectedType(StocktakingDraftType.DEFAULT)}
						/>
						<small className="text-muted" style={{ marginLeft: "19px" }}>
							{t("enums.stocktaking.drafts.label.DEFAULT")}
						</small>
					</div>
					<div className="d-flex flex-column mb-3">
						<Form.Check
							label={t("enums.stocktaking.drafts.type.SCALES")}
							type="radio"
							id="SCALES"
							className="form-check"
							checked={selectedType === "SCALES"}
							onChange={() => setSelectedType(StocktakingDraftType.SCALES)}
						/>
						<small className="text-muted" style={{ marginLeft: "19px" }}>
							{t("enums.stocktaking.drafts.label.SCALES")}
						</small>
					</div>
					<div className="d-flex flex-column mb-2">
						<Form.Check
							label={t("enums.stocktaking.drafts.type.MOBILE")}
							type="radio"
							id="MOBILE"
							className="form-check"
							checked={selectedType === "MOBILE"}
							onChange={() => setSelectedType(StocktakingDraftType.MOBILE)}
						/>
						<small className="text-muted" style={{ marginLeft: "19px" }}>
							{t("enums.stocktaking.drafts.label.MOBILE")}
						</small>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
						{t("lib:common.action.add")}
					</ButtonLoading>
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default CreateStocktakingDraftModalForm;
